import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * サーバ到達性の判定結果。
 *
 * 「届かない」を細かく割るのが要点。院内 LAN からのサーバ障害時に
 * 「Tailscale を確認してください」と案内すると、利用者は誤った復旧手順に時間を使う。
 * 逆に「遅いだけ」を到達不能と断定すると、**接続済みの VPN を切らせて**しまい、
 * 切った瞬間に本当に到達不能になる（案内が自己成就する）。
 */
export type ConnectionStatus =
  /** 初回プローブがまだ終わっていない */
  | 'checking'
  /** 正常 */
  | 'online'
  /** サーバの箱に届いていない（回線 / Tailscale / VPN の問題） */
  | 'server-unreachable'
  /** 届いてはいるが応答が返らない（低速回線・輻輳）。設定は触らせない */
  | 'network-slow'
  /** 箱には届くがアプリが応答しない（サーバ側の障害） */
  | 'api-unavailable';

/**
 * プローブ 1 段の実測結果。
 *
 * status だけでは「どの段で落ちたか」が復元できない。
 * 例えば 'network-slow' は 1 段目のタイムアウトでも 2 段目のタイムアウトでも起きるため、
 * status から内訳を逆算すると **成否が逆に表示される**。UI に事実を出すには段ごとに持つ。
 */
export type ProbeStageState =
  /** 応答があった */
  | 'ok'
  /** 失敗が確定した */
  | 'ng'
  /** 時間内に応答が返らなかった */
  | 'slow'
  /** 手前の段で失敗したので実行していない */
  | 'unchecked'
  /** 実行したが、原因を切り分けられなかった */
  | 'unknown';

/** 2 段プローブの結果。status と、その根拠になった段ごとの実測 */
export interface ConnectionProbe {
  status: ConnectionStatus;
  /** 1 段目 /healthz（サーバの箱まで届いたか） */
  edge: ProbeStageState;
  /** 2 段目 /api/health（箱の中のアプリが応答したか） */
  api: ProbeStageState;
}

/** 初回プローブ完了前の状態 */
const INITIAL_PROBE: ConnectionProbe = { status: 'checking', edge: 'unchecked', api: 'unchecked' };

/** nginx が静的に 200 を返すエンドポイント。到達性の 1 段目 */
const EDGE_PROBE_URL = '/healthz';

/** backend（DB 疎通込み）のヘルスチェック。到達性の 2 段目 */
const API_PROBE_URL = '/api/health';

/** 1 回のプローブのタイムアウト */
const PROBE_TIMEOUT_MS = 5000;

/** 復旧待ちの再プローブ間隔（初回）。以降は指数的に伸ばす */
const RETRY_BASE_INTERVAL_MS = 15000;

/** 再プローブ間隔の上限。障害中のサーバに負荷を上乗せし続けない */
const RETRY_MAX_INTERVAL_MS = 120000;

/** バックオフの段数上限（連続失敗 1〜4 回で 15s → 30s → 60s → 120s、以降は 120s 据え置き） */
const RETRY_MAX_STEP = 4;

/** 自動プローブの最短間隔（イベント連打でリクエストを増やさない） */
const MIN_PROBE_INTERVAL_MS = 5000;

/**
 * axios がレスポンスなしで失敗したときに発火する独自イベント名。
 * utils/axiosInterceptor.js から dispatch される。
 */
export const NETWORK_ERROR_EVENT = 'app:network-error';

/** AbortController による中断（＝タイムアウト）か */
const isTimeoutError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

/** タイムアウト付き fetch。中断も「失敗」として throw する。 */
const fetchWithTimeout = async (url: string, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { method: 'GET', cache: 'no-store', signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

/**
 * 2 段プローブで接続状態を判定する。
 *
 * 判定の分かれ目は「なぜ失敗したか」。
 * - 1 段目がトランスポート層で落ちた → 箱に届いていない = 'server-unreachable'
 * - 1 段目がタイムアウト → 繋がってはいるが遅い = 'network-slow'（VPN を切らせない）
 * - 1 段目に HTTP 応答があった（404/502 でも）→ 箱には届いている。2 段目へ進む
 * - 2 段目がトランスポート層で落ちた → 1 段目の後に回線が落ちた可能性が高いので
 *   'server-unreachable' に倒す。ここで 'api-unavailable' にすると
 *   「Tailscale や Wi-Fi の設定を変える必要はありません」と**逆の案内**を出してしまう。
 *   ただし回線とアプリのどちらが落ちたかは切り分けられないので、段の結果は 'unknown'。
 *   ここを 'ng' と断定すると、UI が「アプリが落ちた」と嘘をつく
 * - 2 段目が非 200 → サーバ側の障害 = 'api-unavailable'
 */
export const probeConnection = async (
  timeoutMs: number = PROBE_TIMEOUT_MS
): Promise<ConnectionProbe> => {
  try {
    await fetchWithTimeout(EDGE_PROBE_URL, timeoutMs);
  } catch (error) {
    return isTimeoutError(error)
      ? { status: 'network-slow', edge: 'slow', api: 'unchecked' }
      : { status: 'server-unreachable', edge: 'ng', api: 'unchecked' };
  }

  try {
    const response = await fetchWithTimeout(API_PROBE_URL, timeoutMs);
    return response.ok
      ? { status: 'online', edge: 'ok', api: 'ok' }
      : { status: 'api-unavailable', edge: 'ok', api: 'ng' };
  } catch (error) {
    return isTimeoutError(error)
      ? { status: 'network-slow', edge: 'ok', api: 'slow' }
      : { status: 'server-unreachable', edge: 'ok', api: 'unknown' };
  }
};

export interface ServerReachabilityState {
  /** 直近のプローブ結果（status と段ごとの実測） */
  probe: ConnectionProbe;
  /** プローブ実行中 */
  isChecking: boolean;
  /** ［再試行］用。スロットルを無視して即プローブする */
  recheck: () => void;
}

/**
 * サーバに到達できているかを監視する。
 *
 * 【プローブの発火条件】
 * - マウント時に 1 回
 * - online / offline イベント（online は復旧の確実なシグナルなのでスロットルを無視する。
 *   offline は確実な負のシグナルなので、プローブせず即座に落とす）
 * - visibilitychange で可視に戻ったとき
 * - NETWORK_ERROR_EVENT（axios のネットワークエラー）
 * - online でない間だけ、指数バックオフ（15s → 120s）で再試行。
 *   **タブが非表示の間は再試行しない**（復帰時に visibilitychange で必ず 1 回走る）。
 *   これが無いと、障害中のサーバに対して全タブが毎分 4 回 DB 疎通付きの
 *   /api/health を打ち続け、自分で負荷を上乗せすることになる。
 *
 * 【enabled を引数で受け取る理由】
 * 静的デモ（Cloudflare Pages）には /healthz も /api/health も無く、
 * プローブすると常に異常と判定されてしまう。呼び出し側で切れるようにしておく。
 */
export const useServerReachability = (enabled: boolean): ServerReachabilityState => {
  const [probe, setProbe] = useState<ConnectionProbe>(INITIAL_PROBE);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  /**
   * 連続失敗回数。**飽和させずに単調増加させる**。
   * ここを上限で頭打ちにすると、上限到達後は status も retry 値も変化せず
   * 再試行 effect が再実行されない ＝ **自動再試行が永久に止まる**（復旧しても案内が消えない）。
   * 遅延の頭打ちは delay 計算側で行う。
   */
  const [failureCount, setFailureCount] = useState<number>(0);
  /** 直近にプローブを開始した時刻（スロットル用） */
  const lastProbeAtRef = useRef<number>(0);
  /** 実行中のプローブがあるか（多重実行の抑止） */
  const inFlightRef = useRef<boolean>(false);
  /** スロットルで間引いた分の遅延実行タイマー（末尾実行） */
  const trailingProbeRef = useRef<number | null>(null);
  /** 最新のプローブ関数。イベントハンドラと recheck から共通で呼ぶ */
  const probeRef = useRef<(force: boolean) => void>(() => undefined);

  useEffect(() => {
    if (!enabled) return undefined;

    let disposed = false;

    const runProbe = async (force: boolean): Promise<void> => {
      if (inFlightRef.current) return;
      const now = Date.now();
      const sinceLastProbe = now - lastProbeAtRef.current;
      if (!force && sinceLastProbe < MIN_PROBE_INTERVAL_MS) {
        // 間引いた要求を捨てると、**保留中の作業がどこにも残らない**。
        // status が 'online' の間は再試行タイマーが張られていないため、
        // 起動直後に API が一斉に失敗する（＝サーバ障害の最も典型的な出方）と、
        // 全部が 5 秒窓に入って消え、案内が一度も出ないまま空の画面が残る。
        // 先頭を実行して末尾で 1 回だけ拾い直す。
        if (trailingProbeRef.current === null) {
          trailingProbeRef.current = window.setTimeout(() => {
            trailingProbeRef.current = null;
            if (!disposed) void runProbe(false);
          }, MIN_PROBE_INTERVAL_MS - sinceLastProbe);
        }
        return;
      }

      lastProbeAtRef.current = now;
      inFlightRef.current = true;
      setIsChecking(true);
      try {
        const next = await probeConnection();
        if (disposed) return;
        setProbe(next);
        setFailureCount((count) => (next.status === 'online' ? 0 : count + 1));
      } finally {
        // 破棄済みなら触らない。cleanup が既に解除しており、ここで書き戻すと
        // **後から着地した古いプローブが、実行中の新しいプローブの抑止を解いてしまう**。
        if (!disposed) {
          inFlightRef.current = false;
          setIsChecking(false);
        }
      }
    };

    probeRef.current = (force: boolean) => {
      void runProbe(force);
    };

    const handleAutoProbe = (): void => probeRef.current(false);

    // online は「利用者が案内どおり Wi-Fi / Tailscale を直した」という最も確実な合図。
    // スロットルで捨てると、正しく対処したのに次のバックオフ（最大 120 秒）まで案内が残る。
    const handleOnline = (): void => probeRef.current(true);

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') handleAutoProbe();
    };

    // navigator.onLine は「true なのに繋がらない」ことはあるが、
    // 「false なのに繋がる」ことはない。offline は信じて即座に落とす
    // （どうせ失敗するプローブを 1 往復させずに案内を出す）。
    // ただし実測はしていないので、段の内訳は 'unknown'（＝判定できていない）にする。
    const handleOffline = (): void =>
      setProbe({ status: 'server-unreachable', edge: 'unknown', api: 'unchecked' });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(NETWORK_ERROR_EVENT, handleAutoProbe);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    void runProbe(true);

    return () => {
      disposed = true;
      // 実行中のプローブは結果を捨てる（disposed）ので、多重実行の抑止も一緒に解く。
      // 解かないと、**再マウント直後の初回プローブが握り潰されて 'checking' のまま**
      // 固定される（'checking' には再試行タイマーも付かないので永久に案内が出ない）。
      // React 18 の StrictMode は必ずこの経路を通るため、開発時に必ず踏む。
      inFlightRef.current = false;
      // 飛行中のまま破棄されると finally が走らないので、ここで確実に戻す。
      // 残ると［再試行］が「確認中…」のまま永久に押せなくなる。
      setIsChecking(false);
      if (trailingProbeRef.current !== null) {
        window.clearTimeout(trailingProbeRef.current);
        trailingProbeRef.current = null;
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(NETWORK_ERROR_EVENT, handleAutoProbe);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  // 復旧待ちの間だけ、間隔を伸ばしながら再試行する（online に戻ったら止まる）
  useEffect(() => {
    if (!enabled || probe.status === 'online' || probe.status === 'checking') return undefined;

    // 連続失敗 1 回目は RETRY_BASE_INTERVAL_MS、以降 2 倍ずつ伸ばして上限で頭打ち。
    const delay = Math.min(
      RETRY_MAX_INTERVAL_MS,
      RETRY_BASE_INTERVAL_MS * 2 ** Math.max(0, Math.min(failureCount, RETRY_MAX_STEP) - 1)
    );
    const timer = window.setTimeout(() => {
      // 非表示中は打たない。復帰時に visibilitychange で必ず 1 回走るので取りこぼさない。
      if (document.hidden) return;
      probeRef.current(true);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [enabled, probe.status, failureCount]);

  const recheck = useCallback((): void => {
    probeRef.current(true);
  }, []);

  return { probe, isChecking, recheck };
};
